import { computed } from 'vue'

import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { useDsAttendanceStore } from '@/pinia-store/useDsAttendanceStore'
import { AuthorizationPermissions, AuthorizationResourcesAndSubjects } from '@/types/authorization'

/**
 * Q31 — permissão PRÓPRIA para editar as seções NOVAS da política de Registro de
 * Plantão (Registro de Atividade e Precedência das fontes). É mais restrita que
 * `attendance_configuring`: quem já editava política continua editando vigência,
 * modalidade e tolerâncias.
 *
 * O cadeado é ergonomia, não segurança: o backend rejeita o payload de qualquer
 * jeito (§1.6). Observação herdada do padrão do repo: com a feature flag de
 * permissões desligada, `checkAnyRessources` devolve `true` e nada fica travado.
 */
export function useRegistrationPolicyPermissions() {
  const permissionsStore = usePermissionsStore()
  const dsAttendanceStore = useDsAttendanceStore()

  const hasRegistrationPermission = computed(() =>
    permissionsStore.checkAnyRessources({
      permission: AuthorizationPermissions.AttendanceRegistrationConfiguring,
      resourceType: AuthorizationResourcesAndSubjects.DsAttendance
    })
  )

  /**
   * O cadeado só existe DEPOIS que o backend da F2 sobe (feature detection por
   * `registration-settings`). Antes disso `attendance_registration_configuring`
   * ainda não foi publicada pelo SpiceDB e `checkAnyRessources` devolveria `false`
   * para todo mundo — travando no dia do deploy do web uma tela que hoje é
   * editável. Sem backend F2: comportamento de hoje, pixel a pixel.
   */
  const canEditRegistrationSection = computed(
    () => !dsAttendanceStore.isRegistrationBackendAvailable || hasRegistrationPermission.value
  )

  /**
   * A seção "Auditoria" ("Quem confere a folha", "Aprovação automática", "Revisão
   * humana por amostragem") está em PROD desde a 1.10.647 e NÃO entra no cadeado da
   * Q31: travá-la por uma permissão que o backend ainda não declara a tiraria de
   * todo mundo. Ela só vira leitura quando o backend PROVA a recusa, devolvendo 403
   * no salvamento.
   */
  const canEditAuditSection = computed(() => !dsAttendanceStore.isRegistrationSectionForbidden)

  return { canEditRegistrationSection, canEditAuditSection }
}
